import { EMessageChannel } from 'src/services/api/messages/types';

type SupportedSignatureChannel = EMessageChannel.SMS | EMessageChannel.EMAIL | EMessageChannel.FACEBOOK_MESSENGER;

type SignatureStorageChannel = EMessageChannel.SMS | EMessageChannel.EMAIL;

/**
 * Maps every signature-capable channel to the settings key its signature is actually
 * stored under. Facebook Messenger has no storage of its own (LandlordUserSettings only
 * has email and sms signature columns), so it aliases to the SMS signature rather than
 * getting a dedicated field.
 */
const SIGNATURE_STORAGE_CHANNEL_BY_CHANNEL: Record<SupportedSignatureChannel, SignatureStorageChannel> = {
    [EMessageChannel.EMAIL]: EMessageChannel.EMAIL,
    [EMessageChannel.SMS]: EMessageChannel.SMS,
    [EMessageChannel.FACEBOOK_MESSENGER]: EMessageChannel.SMS,
};

export const isSupportedSignatureChannel = (channel: EMessageChannel): channel is SupportedSignatureChannel => {
    return channel in SIGNATURE_STORAGE_CHANNEL_BY_CHANNEL;
};

export const getSignatureStorageChannel = (channel: SupportedSignatureChannel): SignatureStorageChannel => {
    return SIGNATURE_STORAGE_CHANNEL_BY_CHANNEL[channel];
};

export type GetUserSettingsResponse = {
    settings: {
        auto_archive_lead_after_days: number;

        signatures: {
            sms: {
                is_enabled: boolean;
                text: string;
            };

            email: {
                is_enabled: boolean;
                text: string;
            };
        };

        notifications: {
            new_message: 'all' | 'owner_only' | 'off';
        };

        conversations_hidden_lead_label_ids: number[] | null;

        analytics_hidden_lead_label_ids: number[] | null;

        is_achievements_enabled: boolean;

        /**
         * When calendar integration is enabled, whether leads are automatically added as invitees to calendar events
         */
        is_auto_invite_lead_to_tour: boolean;

        /**
         * When enabled, inbound messages in non-English languages are automatically translated to English (AI-powered).
         */
        is_inbound_message_translate_enabled: boolean;

        /**
         * IANA timezone used for user-local display and scheduling. Null means use the browser/system timezone.
         */
        timezone_override: string | null;

        /**
         * Per-day working hours schedule. 7 entries indexed Monday=0 … Sunday=6.
         * null means no schedule configured (user appears online by default).
         */
        working_hours_schedule: Array<{
            enabled: boolean;
            start?: string;
            end?: string;
        }> | null;
    };
}

export type UserSettings = GetUserSettingsResponse['settings'];
